'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Avatar, Breadcrumbs, Icon } from '@benzinga/core-ui';
import { useBenzingaEdge } from '@benzinga/edge';
import styles from './AccountChrome.module.css';
import { ACCOUNT_SECTIONS, FULL_PAGE_SEGMENTS, SECTION_GROUPS } from './sections';
import { useAccountData } from './AccountDataProvider';

export function AccountChrome({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(['profile', 'common']);
  const pathname = usePathname();
  const { chatIdentity, isLoadingUser, subscriptions, user } = useAccountData();
  const { hasEdgeSubscription } = useBenzingaEdge();

  const activeProducts = subscriptions
    .filter(sub => sub.status === 'active' || sub.status === 'trialing')
    .map(sub => `${sub.product} ${sub.productName}`.toLowerCase());
  const hasPro = activeProducts.some(name => /\bpro\b/.test(name));
  const hasResearch = activeProducts.some(name => name.includes('research'));

  const pathSegment = pathname?.replace(/^\/account\/?/, '').split('/')[0];
  if (pathSegment && FULL_PAGE_SEGMENTS.includes(pathSegment)) {
    return <>{children}</>;
  }

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.displayName || '';
  const groups = Object.entries(SECTION_GROUPS) as [keyof typeof SECTION_GROUPS, { labelKey: string; label: string }][];

  const accountLabel = t('Shell.account', { defaultValue: 'Account', ns: 'profile' });
  const activeSection = ACCOUNT_SECTIONS.find(section => section.href === pathname);
  const breadcrumbs = [
    { href: '/', id: 'home', name: t('Shell.home', { defaultValue: 'Home', ns: 'common' }) },
    { href: '/account', id: 'account', name: accountLabel },
    ...(activeSection
      ? [
          {
            href: activeSection.href,
            id: activeSection.id,
            name: t(activeSection.labelKey, { defaultValue: activeSection.label, ns: 'profile' }),
          },
        ]
      : []),
  ];

  return (
    <div className={`acct-root ${styles.page}`}>
      <div className={styles.wrapper}>
        <Breadcrumbs className={styles.breadcrumbs} data={breadcrumbs} />
        <h1 className={styles.pageTitle}>{accountLabel}</h1>
        <div className={styles.grid}>
          <aside className={styles.sidebar}>
            <div className={styles.sidebarCard}>
              <div className={styles.profileCard}>
                {isLoadingUser ? (
                  <div aria-hidden="true" className={styles.avatarSkeleton} />
                ) : (
                  <Avatar
                    imageClassName={styles.avatarImage}
                    name={user?.displayName ?? ''}
                    photo={chatIdentity?.avatar ?? ''}
                    pixels={96}
                  />
                )}
                <div className={styles.profileMeta}>
                  <div className={styles.profileName}>{isLoadingUser ? ' ' : fullName}</div>
                  <div className={styles.profileEmail}>{isLoadingUser ? ' ' : (user?.email ?? '')}</div>
                  {!isLoadingUser ? (
                    <div className={styles.profileBadges}>
                      {hasEdgeSubscription ? <span className="acct-pill acct-pill--edge">EDGE</span> : null}
                      {hasPro ? <span className="acct-pill acct-pill--pro">PRO</span> : null}
                      {hasResearch ? <span className="acct-pill acct-pill--research">RESEARCH</span> : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <nav
                aria-label={t('Shell.account-settings', { defaultValue: 'Account settings', ns: 'profile' })}
                className={styles.nav}
              >
                {groups.map(([groupId, group]) => {
                  const sections = ACCOUNT_SECTIONS.filter(section => section.group === groupId);
                  if (sections.length === 0) return null;
                  return (
                    <React.Fragment key={groupId}>
                      <div className={styles.navGroupLabel}>
                        {t(group.labelKey, { defaultValue: group.label, ns: 'profile' })}
                      </div>
                      {sections.map(section => {
                        const isActive = pathname === section.href;
                        return (
                          <Link
                            aria-current={isActive ? 'page' : undefined}
                            className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                            href={section.href}
                            key={section.id}
                          >
                            <span className={styles.navIcon}>
                              <Icon icon={isActive ? section.activeIcon : section.icon} />
                            </span>
                            {t(section.labelKey, { defaultValue: section.label, ns: 'profile' })}
                          </Link>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </nav>
            </div>
          </aside>
          <main className={styles.panel} key={pathname}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
